import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  FileUploadOutlined as ImportIcon,
  IosShareOutlined as ExportIcon,
  DownloadOutlined as DownloadIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../apiBase';
import { usePagePermissions } from '../context/AuthContext';

export default function CodeToCodePurpose() {
  const { canCreate, canUpdate, canDelete, canImport, canExport } = usePagePermissions('code_to_code_purpose');
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [query, setQuery] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ fromCode: '', toCode: '', purpose: '' });
  const [deleteId, setDeleteId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const fileRef = useRef(null);

  const loadVendors = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/vendors`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      const data = await res.json().catch(() => ({}));
      setVendors(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setVendors([]);
    }
  };

  const loadRows = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/code-purpose`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      const data = await res.json().catch(() => ([]));
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setToast({ open: true, message: e?.message || 'Failed to load', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    loadVendors();
  }, []);

  const filteredRows = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.fromCode, r.toCode, r.purpose, r.createdAt].some((v) => String(v || '').toLowerCase().includes(q))
    );
  }, [rows, query]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const openAdd = () => {
    if (!canCreate) {
      setToast({ open: true, message: 'You do not have create permission for this page', severity: 'error' });
      return;
    }
    setEditingId(null);
    setForm({ fromCode: '', toCode: '', purpose: '' });
    setEditOpen(true);
  };
  const openEdit = (row) => {
    if (!canUpdate) {
      setToast({ open: true, message: 'You do not have update permission for this page', severity: 'error' });
      return;
    }
    setEditingId(row.id);
    setForm({ fromCode: row.fromCode || '', toCode: row.toCode || '', purpose: row.purpose || '' });
    setEditOpen(true);
  };

  const save = async () => {
    const body = { ...form };
    if (!body.fromCode || !body.toCode || !body.purpose) {
      setToast({ open: true, message: 'All fields are required', severity: 'error' });
      return;
    }
    if (editingId && !canUpdate) {
      setToast({ open: true, message: 'You do not have update permission for this page', severity: 'error' });
      return;
    }
    if (!editingId && !canCreate) {
      setToast({ open: true, message: 'You do not have create permission for this page', severity: 'error' });
      return;
    }
    setLoading(true);
    try {
      if (editingId) {
        const res = await fetch(`${API_BASE_URL}/api/code-purpose/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Update failed');
        setToast({ open: true, message: 'Updated', severity: 'success' });
      } else {
        const res = await fetch(`${API_BASE_URL}/api/code-purpose`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Create failed');
        setToast({ open: true, message: 'Added', severity: 'success' });
      }
      setEditOpen(false);
      setEditingId(null);
      await loadRows();
    } catch (e) {
      setToast({ open: true, message: e?.message || 'Action failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (id) => {
    if (!canDelete) {
      setToast({ open: true, message: 'You do not have delete permission for this page', severity: 'error' });
      return;
    }
    setDeleteId(id);
    setDeleteOpen(true);
  };
  const doDelete = async () => {
    if (!deleteId) return;
    if (!canDelete) {
      setToast({ open: true, message: 'You do not have delete permission for this page', severity: 'error' });
      setDeleteOpen(false);
      setDeleteId(null);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/code-purpose/${deleteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Delete failed');
      setRows((prev) => prev.filter((r) => r.id !== deleteId));
      setToast({ open: true, message: 'Deleted', severity: 'info' });
    } catch (e) {
      setToast({ open: true, message: e?.message || 'Delete failed', severity: 'error' });
    } finally {
      setDeleteOpen(false);
      setDeleteId(null);
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!canExport) {
      setToast({ open: true, message: 'You do not have export permission for this page', severity: 'error' });
      return;
    }
    const header = ['From Code', 'To Code', 'Purpose Of Payment', 'Created At'];
    const data = rows.map((r) => [r.fromCode, r.toCode, r.purpose, r.createdAt]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'code_to_code_purpose.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadTemplate = () => {
    if (!canExport) {
      setToast({ open: true, message: 'You do not have export permission for this page', severity: 'error' });
      return;
    }
    const header = ['From Code', 'To Code', 'Purpose Of Payment'];
    const ws = XLSX.utils.aoa_to_sheet([header, ['F001', 'V123', 'AR']]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'code_to_code_purpose_template.xlsx');
  };

  const importFile = async (file) => {
    if (!canImport) {
      setToast({ open: true, message: 'You do not have import permission for this page', severity: 'error' });
      return;
    }
    if (!file) return;
    setLoading(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { header: 0, raw: false, defval: '' });
      const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const items = json
        .map((r) => {
          const km = {};
          Object.keys(r).forEach((k) => (km[norm(k)] = r[k]));
          const fromCode = String(km[norm('From Code')] || km['fromcode'] || '').trim();
          const toCode = String(km[norm('To Code')] || km['tocode'] || '').trim();
          const purpose = String(km[norm('Purpose Of Payment')] || km['purpose'] || '').trim();
          if (!fromCode || !toCode || !purpose) return null;
          return { fromCode, toCode, purpose };
        })
        .filter(Boolean);
      if (items.length === 0) throw new Error('No valid rows found');
      const res = await fetch(`${API_BASE_URL}/api/code-purpose/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        body: JSON.stringify({ items }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Import failed');
      setToast({
        open: true,
        message: `Import completed: ${data?.inserted ?? 0} added, ${data?.skipped ?? 0} skipped`,
        severity: 'success',
      });
      await loadRows();
    } catch (e) {
      setToast({ open: true, message: e?.message || 'Import failed', severity: 'error' });
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Layout title="Code To Code Purpose">
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Typography variant="h4" align="center" sx={{ fontWeight: 500, letterSpacing: 0.5, mb: 3 }}>
          Code To Code Purpose
        </Typography>

        <Stack direction="row" spacing={1.5} sx={{ mb: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          {canExport && (
            <Button
              startIcon={<ExportIcon />}
              variant="contained"
              color="success"
              onClick={exportCsv}
              disabled={loading}
            >
              Export
            </Button>
          )}
          {canExport && (
            <Button
              startIcon={<DownloadIcon />}
              variant="contained"
              color="info"
              onClick={downloadTemplate}
              disabled={loading}
            >
              Download Template
            </Button>
          )}
          {canImport && (
            <Button variant="contained" component="label" disabled={loading}>
              Import
              <input
                hidden
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => importFile(e.target.files?.[0])}
              />
            </Button>
          )}
          {canCreate && (
            <Button startIcon={<AddIcon />} variant="contained" onClick={openAdd} disabled={loading}>
              Add
            </Button>
          )}
        </Stack>

        <Paper sx={{ p: 2, borderRadius: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems="center">
            <TextField
              size="small"
              placeholder="Search code / purpose…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: { xs: '100%', md: 320 } }}
            />
            {query.trim() && (
              <Button
                variant="text"
                color="inherit"
                onClick={() => {
                  setQuery('');
                  setPage(0);
                }}
              >
                Clear
              </Button>
            )}
            <Chip
              size="small"
              label={
                rows.length === filteredRows.length ? `${rows.length} total` : `Showing ${filteredRows.length} of ${rows.length}`
              }
            />
          </Stack>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sr No</TableCell>
                  <TableCell>From Code</TableCell>
                  <TableCell>To Code</TableCell>
                  <TableCell>Purpose Of Payment</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((r, idx) => (
                  <TableRow key={r.id}>
                    <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{r.fromCode}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{r.toCode}</TableCell>
                    <TableCell>{r.purpose}</TableCell>
                    <TableCell>{r.createdAt ? String(r.createdAt).slice(0, 16).replace('T', ' ') : '-'}</TableCell>
                    <TableCell align="right">
                      {canUpdate && (
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(r)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => confirmDelete(r.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>

        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>{editingId ? 'Edit' : 'Add'} Code Purpose</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <TextField
                label="From Code"
                select
                value={form.fromCode}
                onChange={(e) => setForm((p) => ({ ...p, fromCode: e.target.value }))}
              >
                {vendors.map((v) => (
                  <MenuItem key={v.id} value={v.firmVendorCode}>
                    {v.firmVendorCode} — {v.firmVendorName}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="To Code"
                select
                value={form.toCode}
                onChange={(e) => setForm((p) => ({ ...p, toCode: e.target.value }))}
              >
                {vendors.map((v) => (
                  <MenuItem key={v.id} value={v.firmVendorCode}>
                    {v.firmVendorCode} — {v.firmVendorName}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Purpose Of Payment"
                value={form.purpose}
                onChange={(e) => setForm((p) => ({ ...p, purpose: e.target.value }))}
                placeholder="e.g., MV, AR"
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setEditOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button onClick={save} variant="contained" disabled={loading}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>Delete Record?</DialogTitle>
          <DialogContent>
            <Typography sx={{ color: 'text.secondary' }}>This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDeleteOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button onClick={doDelete} variant="contained" color="error" disabled={loading}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={toast.open}
          autoHideDuration={2500}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setToast((t) => ({ ...t, open: false }))} severity={toast.severity} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
}
